/**
 * User Database API Service
 * ========================
 * Lightweight API for logging user events to the backend database.
 * All methods are fire-and-forget - errors don't affect app functionality.
 */

import axios, { InternalAxiosRequestConfig } from 'axios';
import { msalInstance, silentRedirectUri } from '../msalInstance';
import { apiTokenRequest } from '../authConfig';
import { requiresInteractiveConsent, triggerConsentRedirect } from '../utils/apiAuthHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

console.log('[UserDbApi] Configured with API_URL:', API_URL);

// Create axios instance with base configuration
const userDbClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // Increased timeout for remote API calls (critical for authorization)
});

// Attach Azure AD Bearer token when API scope is configured
userDbClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!apiTokenRequest) return config;
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return config;
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account: accounts[0],
      redirectUri: silentRedirectUri,
    });
    config.headers.Authorization = `Bearer ${response.accessToken}`;
  } catch (error) {
    if (requiresInteractiveConsent(error)) {
      await triggerConsentRedirect();
    } else {
      console.error(
        '[UserDbApi] Failed to acquire API token:',
        error instanceof Error ? error.message : error
      );
    }
  }
  return config;
});

export interface AuthorizationCheckResponse {
  authorized: boolean;
  method: 'database_whitelist' | 'none';
  source: 'database' | 'denied';
  email: string;
  timestamp: string;
}

/**
 * User Database API - Simplified for database whitelist only
 */
export const userDbApi = {
  /**
   * Ensure user exists in database (called on login)
   * Fire-and-forget: errors are logged but don't affect auth flow
   */
  ensureUserExists: async (email: string, displayName?: string): Promise<void> => {
    try {
      userDbClient.post('/user/ensure', { email, display_name: displayName }).catch((error) => {
        console.warn('[USER_DB] Failed to ensure user exists:', error.message);
      });
    } catch (error) {
      console.warn('[USER_DB] Error initiating user creation:', error);
    }
  },

  /**
   * Add user to waitlist
   * Sets is_waitlist=TRUE for the user in the database
   * 
   * @param email - User email
   * @param displayName - User display name (optional)
   * @returns Promise with status and message
   */
  joinWaitlist: async (email: string, displayName?: string): Promise<{
    status: string;
    message: string;
    email?: string;
    is_waitlist?: boolean;
  }> => {
    try {
      console.log(`📋 [USER_DB] Adding user to waitlist: ${email}`);
      const response = await userDbClient.post('/user/join-waitlist', { 
        email, 
        display_name: displayName 
      });
      console.log(`✅ [USER_DB] Successfully added to waitlist:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [USER_DB] Failed to add user to waitlist:', error);
      throw error;
    }
  },

  /**
   * Check authorization via database whitelist
   * This is NOT fire-and-forget - authorization is critical
   * Includes retry logic for network resilience
   * 
   * @param email - User email to check
   * @returns Promise<AuthorizationCheckResponse> - Authorization result
   * @throws Error if request fails after all retries
   */
  checkAuthorization: async (email: string): Promise<AuthorizationCheckResponse> => {
    const maxRetries = 2; // Try 3 times total (1 initial + 2 retries)
    let lastError: any = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const startTime = Date.now();
      
      if (attempt === 0) {
        console.log(`🔐 [USER_DB] Starting authorization check for: ${email}`);
        console.log(`🔐 [USER_DB] API endpoint: ${API_URL}/auth/check`);
      } else {
        console.log(`🔄 [USER_DB] Retry attempt ${attempt}/${maxRetries} for: ${email}`);
      }
      
      try {
        const response = await userDbClient.post('/auth/check', { email });
        const duration = Date.now() - startTime;
        
        if (attempt > 0) {
          console.log(`✅ [USER_DB] Authorization check successful on retry ${attempt} (${duration}ms)`);
        } else {
          console.log(`✅ [USER_DB] Authorization check successful (${duration}ms):`, {
            authorized: response.data.authorized,
            method: response.data.method,
            source: response.data.source
          });
        }
        
        return response.data;
      } catch (error: any) {
        const duration = Date.now() - startTime;
        lastError = error;
        
        // Check if this is a retryable error
        const isRetryable = 
          error.code === 'ECONNABORTED' || // Timeout
          error.code === 'ETIMEDOUT' ||
          error.code === 'ECONNRESET' ||
          error.code === 'ENOTFOUND' ||
          !error.response || // Network error
          (error.response && error.response.status >= 500); // Server error
        
        console.error(`❌ [USER_DB] Authorization check failed (attempt ${attempt + 1}/${maxRetries + 1}, ${duration}ms):`, {
          error: error.message,
          code: error.code,
          status: error.response?.status,
          isRetryable
        });
        
        // If not retryable or last attempt, throw
        if (!isRetryable || attempt === maxRetries) {
          throw new Error(`Authorization check failed after ${attempt + 1} attempt(s): ${error.message || 'Unknown error'}`);
        }
        
        // Wait before retry (exponential backoff: 1s, 2s)
        const delay = 1000 * Math.pow(2, attempt);
        console.log(`⏳ [USER_DB] Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // Should never reach here, but just in case
    throw new Error(`Authorization check failed: ${lastError?.message || 'Unknown error'}`);
  },
};

export default userDbApi;
